/**
 * The work replay's shapes, from avrio-rio: the recording's format v1
 * (client/react/docs/work-replay.md, services/workspace/replay/format.ts) and
 * the hiring team's view of it (docs/work-assets.md, "The hiring team's view"),
 * which avrio-backend passes through from
 * GET /api/jobs/{jobId}/candidates/{candidateId}/work-replay.
 *
 * Copied, not shared: the two apps have no common package. A field Rio adds
 * later is ignored here until it is read on purpose.
 */

export type WorkspaceLanguage =
  | 'python'
  | 'javascript'
  | 'typescript'
  | 'java'
  | 'kotlin'
  | 'scala'
  | 'csharp'
  | 'cpp'
  | 'c'
  | 'go'
  | 'rust'
  | 'ruby'
  | 'php'
  | 'swift'
  | 'sql'
  | 'r'
  | 'bash';

export const LANGUAGE_LABELS: Readonly<Record<WorkspaceLanguage, string>> = {
  python: 'Python',
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  java: 'Java',
  kotlin: 'Kotlin',
  scala: 'Scala',
  csharp: 'C#',
  cpp: 'C++',
  c: 'C',
  go: 'Go',
  rust: 'Rust',
  ruby: 'Ruby',
  php: 'PHP',
  swift: 'Swift',
  sql: 'SQL',
  r: 'R',
  bash: 'Bash',
};

/** A canvas task's kind stands in for the language: it has none. */
export type CanvasKind = 'whiteboard' | 'design';

export const CANVAS_LABELS: Readonly<Record<CanvasKind, string>> = {
  whiteboard: 'Whiteboard',
  design: 'Design canvas',
};

export type CanvasElementType =
  | 'rect'
  | 'ellipse'
  | 'diamond'
  | 'text'
  | 'arrow'
  | 'line'
  | 'frame'
  | 'image_placeholder';

/** One part of a drawing (avrio-rio docs/workspace-canvas.md). */
export interface CanvasElement {
  id: string;
  type: CanvasElementType;
  x: number;
  y: number;
  w: number;
  h: number;
  text?: string;
  /** An arrow's (or line's) own label. */
  label?: string;
  startId?: string;
  endId?: string;
}

export type ChunkKind = 'events' | 'audio' | 'screen';
export const CHUNK_KINDS: readonly ChunkKind[] = ['events', 'audio', 'screen'];

export type ScreenFrameReason = 'left' | 'away' | 'returned';

export interface ReplayFile {
  name: string;
  content: string;
  editable: boolean;
}

/** CodeMirror's ChangeSet.toJSON(): kept lengths as numbers, replacements as [deleted length, ...inserted lines]. */
export type ChangeJSON = (number | [number, ...string[]])[];

/** One NDJSON line of a recording: `t` is milliseconds since it started. */
export type ReplayLine =
  | {
      t: number;
      type: 'header';
      format: 'avrio-work-replay';
      version: 1;
      taskId: string;
      title: string;
      language: WorkspaceLanguage | CanvasKind;
      label: string;
      kind?: 'code' | CanvasKind;
      startedAt: string;
      voice: boolean;
      screen?: boolean;
    }
  | {
      t: number;
      type: 'snapshot';
      reason: 'start' | 'keyframe' | 'language' | 'resume';
      language: WorkspaceLanguage;
      files: ReplayFile[];
      active: string;
    }
  | { t: number; type: 'change'; file: string; changes: ChangeJSON }
  | {
      t: number;
      type: 'canvas_snapshot';
      reason: 'start' | 'change' | 'resume';
      elements: CanvasElement[];
    }
  | { t: number; type: 'file_switch'; file: string }
  | { t: number; type: 'selection'; file: string; anchor: number; head: number }
  | { t: number; type: 'run' }
  | {
      t: number;
      type: 'run_result';
      passed: number;
      failed: number;
      failedTests: string[];
      error: string | null;
    }
  | { t: number; type: 'assist_prompt'; text: string }
  | { t: number; type: 'assist_reply'; text: string }
  | {
      t: number;
      type: 'insert_from_assistant';
      file: string;
      chars: number;
      via: 'replace' | 'paste';
    }
  | { t: number; type: 'constraint_shown'; text: string }
  | { t: number; type: 'rio_message'; text: string }
  | { t: number; type: 'note'; text: string }
  | { t: number; type: 'focus_lost' }
  | { t: number; type: 'focus_returned'; awayMs: number }
  | { t: number; type: 'paste_external'; chars: number; file: string }
  | { t: number; type: 'screen_shared' }
  | { t: number; type: 'screen_share_stopped' }
  | { t: number; type: 'screen_share_resumed' }
  | { t: number; type: 'screen_frame'; n: number; reason: ScreenFrameReason }
  | { t: number; type: 'language_chosen'; language: WorkspaceLanguage }
  | {
      t: number;
      type: 'language_changed';
      from: WorkspaceLanguage;
      to: WorkspaceLanguage;
    }
  | { t: number; type: 'voice_start'; session: number }
  | { t: number; type: 'voice_stop'; session: number }
  | {
      t: number;
      type: 'audio_segment';
      n: number;
      seq: number;
      session: number;
    }
  | { t: number; type: 'done'; passed: number; failed: number; ran: boolean }
  | { t: number; type: 'end'; durationMs: number; truncated: boolean };

/** One chunk the manifest lists: its SHA-256 is lowercase hex. */
export interface ManifestChunk {
  kind: string;
  n: number;
  bytes: number;
  sha256: string;
}

/** The signed manifest (docs/work-assets.md, "The manifest, and Verified by Avrio"), read field by field. */
export interface ReplayManifest {
  assetId: string;
  taskId: string;
  language: string;
  chunks: ManifestChunk[];
  durationMs: number;
  final: { files: number; passed: number; failed: number; ran: boolean };
  createdAt: string | null;
  completedAt: string | null;
  /** The voice walkthrough's transcript, when the service has one. */
  transcript: { t: number; text: string }[];
}

export interface ReplaySignature {
  keyId: string;
  algorithm: string;
  value: string;
}

/** Another replay this job may see for the candidate (phase B shares included). */
export interface GrantedReplay {
  assetId: string;
  grantType: string;
  taskId: string;
  language: string;
  completedAt: string | null;
}

export interface WorkReplayView {
  assetId: string;
  grantType: string;
  manifest: ReplayManifest;
  manifestUrl: string | null;
  signature: ReplaySignature | null;
  urls: { n: number; kind: string; url: string }[];
  granted: GrantedReplay[];
}
